import {
  FiPlusCircle, FiSearch, FiSliders, FiCompass,
  FiFileText, FiCopy, FiFolder, FiDatabase, FiCheckSquare,
  FiUsers, FiGrid, FiShield, FiSettings, FiFeather, FiCreditCard,
  FiHome, FiUserPlus, FiClock, FiUser, FiBookOpen,
} from 'react-icons/fi';
import { TEMPLATE_LIBRARY_ENABLED } from '../../config/features';

// ─── Destination catalog ────────────────────────────────────────────────────
// Labels/descriptions reuse the already-translated navigationWizard.destinations
// i18n namespace so EN + TR stay in sync with the rest of the app.
export const DESTINATIONS = [
  // Create
  {
    path: '/contracts/new', i18nKey: 'newContract', icon: FiPlusCircle,
    keywords: ['create', 'new', 'contract', 'add', 'draft', 'start'],
    intents: ['create'],
  },
  {
    path: '/contract-templates', i18nKey: 'contractTemplates', icon: FiCopy,
    keywords: ['template', 'reuse', 'boilerplate', 'create'],
    intents: ['create', 'manage'],
  },
  {
    path: '/schemas', i18nKey: 'schemas', icon: FiDatabase,
    keywords: ['schema', 'contract type', 'fields', 'structure', 'form'],
    intents: ['create', 'configure'],
  },
  {
    path: '/hr', i18nKey: 'inviteUser', icon: FiUserPlus,
    keywords: ['user', 'invite', 'team', 'member', 'add', 'people'],
    intents: ['create', 'manage'],
  },

  // Find / View
  {
    path: '/dashboard', i18nKey: 'dashboard', icon: FiHome,
    keywords: ['home', 'dashboard', 'overview', 'summary', 'metrics'],
    intents: ['find'],
  },
  {
    path: '/contracts', i18nKey: 'allContracts', icon: FiFileText,
    keywords: ['contract', 'list', 'search', 'find', 'open', 'browse'],
    intents: ['find'],
  },
  {
    path: '/documents', i18nKey: 'documentLibrary', icon: FiFolder,
    keywords: ['document', 'file', 'library', 'pdf', 'attachment'],
    intents: ['find'],
  },
  ...(TEMPLATE_LIBRARY_ENABLED ? [{
    path: '/template-library', i18nKey: 'templateLibrary', icon: FiBookOpen,
    keywords: ['template', 'library', 'standard', 'download', 'nda', 'şablon', 'kütüphane'],
    intents: ['find', 'create'],
  }] : []),
  {
    path: '/approvals', i18nKey: 'approvals', icon: FiCheckSquare,
    keywords: ['approval', 'review', 'pending', 'approve', 'sign off'],
    intents: ['find'],
  },
  {
    path: '/audit', i18nKey: 'auditLog', icon: FiClock,
    keywords: ['audit', 'log', 'history', 'activity', 'trail'],
    intents: ['find'],
  },

  // Manage
  {
    path: '/hr?tab=groups', i18nKey: 'userGroups', icon: FiGrid,
    keywords: ['group', 'team', 'department', 'role'],
    intents: ['manage'],
  },
  {
    path: '/permissions', i18nKey: 'permissions', icon: FiShield,
    keywords: ['permission', 'access', 'rbac', 'role', 'security'],
    intents: ['manage', 'configure'],
  },

  // Configure
  {
    path: '/admin', i18nKey: 'managementPanel', icon: FiSliders,
    keywords: ['admin', 'management', 'tenant', 'system', 'control'],
    intents: ['configure'],
  },
  {
    path: '/signature-integrations', i18nKey: 'signatureIntegrations', icon: FiFeather,
    keywords: ['sign', 'signature', 'docusign', 'adobe', 'integration', 'esign'],
    intents: ['configure'],
  },
  {
    path: '/subscription', i18nKey: 'subscription', icon: FiCreditCard,
    keywords: ['subscription', 'billing', 'plan', 'payment', 'invoice', 'upgrade'],
    intents: ['configure'],
  },
  {
    path: '/settings', i18nKey: 'settings', icon: FiSettings,
    keywords: ['setting', 'branding', 'theme', 'logo', 'organization', 'company', 'tenant', 'workspace', 'currency', 'timezone'],
    intents: ['configure'],
  },
  {
    path: '/profile', i18nKey: 'profile', icon: FiUser,
    keywords: ['profile', 'account', 'preference', 'language', 'password', 'security', 'my settings'],
    intents: ['configure', 'manage'],
  },
];

// ─── Home-screen quick-access cards ─────────────────────────────────────────
// `direct` cards navigate straight to a path; the rest drill into an intent
// (or start the guided tour).
export const INTENTS = [
  { id: 'create', icon: FiPlusCircle, accent: '#6366f1', direct: '/contracts/new' },
  { id: 'find', icon: FiSearch, accent: '#0ea5e9' },
  { id: 'manage', icon: FiUsers, accent: '#10b981' },
  { id: 'configure', icon: FiSliders, accent: '#f59e0b' },
  { id: 'tour', icon: FiCompass, accent: '#8b5cf6', isTour: true },
];

// ─── Example commands under the command bar ─────────────────────────────────
// Labels live in the assistant.examples.* i18n namespace; each is a one-click
// jump, permission-filtered through the same route access check as the cards.
export const EXAMPLE_COMMANDS = [
  { id: 'newContract', path: '/contracts/new' },
  { id: 'pendingActions', path: '/approvals' },
  { id: 'workflows', path: '/flow-management' },
  { id: 'createUser', path: '/hr' },
  { id: 'contractTemplates', path: '/contract-templates' },
];

// ─── Guided tour steps ──────────────────────────────────────────────────────
export const TOUR_STEPS = [
  { id: 'dashboard', path: '/dashboard', icon: FiHome, accent: '#818cf8' },
  { id: 'contracts', path: '/contracts', icon: FiFileText, accent: '#38bdf8' },
  { id: 'documents', path: '/documents', icon: FiFolder, accent: '#34d399' },
  { id: 'approvals', path: '/approvals', icon: FiCheckSquare, accent: '#fbbf24' },
  { id: 'admin', path: '/admin', icon: FiSliders, accent: '#f472b6' },
  { id: 'settings', path: '/settings', icon: FiSettings, accent: '#a78bfa' },
];

// ─── Fuzzy scoring for free-text search ─────────────────────────────────────
export function scoreDestination(dest, query, t) {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const label = t(`navigationWizard.destinations.${dest.i18nKey}.label`).toLowerCase();
  const description = t(`navigationWizard.destinations.${dest.i18nKey}.description`).toLowerCase();
  const haystack = [label, description, ...dest.keywords.map((k) => k.toLowerCase())];
  let score = 0;
  for (const word of q.split(/\s+/).filter(Boolean)) {
    if (label.startsWith(word)) score += 10;
    else if (label.includes(word)) score += 6;
    for (const kw of dest.keywords) {
      if (kw.toLowerCase() === word) score += 5;
      else if (kw.toLowerCase().includes(word)) score += 2;
    }
    if (description.includes(word)) score += 1;
    if (!haystack.some((h) => h.includes(word))) return 0; // every word must appear somewhere
  }
  return score;
}
